// core/data/help/reporting.ts — /help/reporting, authored as data.
//
// The support surface for the platform's central mechanism. Its job is to
// make the rules legible BEFORE someone reports, because the most common way
// a report fails is that it was filed under the wrong idea of what the
// Council does. Everything stated here is behaviour that exists in code —
// the five categories, the accumulation gate, the label, the appeal — not
// aspiration.
//
// Blocks are in READING order — the model keeps it (core/models/help.ts).
//
// Public on the web (no sign-in gate) and served to the app by
// GET /api/help/reporting/ — see core/data/help/index.ts.

import type { HelpDocument, HelpLine } from '@/core/models/help';
import { MODERATION_CATEGORIES, CATEGORY_LABELS } from '@/core/models';

const p = (text: string) => ({ kind: 'paragraph' as const, runs: [{ text }] });
const bullets = (items: HelpLine[]) => ({ kind: 'bullets' as const, items });

export const helpReporting: HelpDocument = {
  id: 'reporting',
  title: 'Help with reporting',
  lead: 'Reporting on TruXSocial is narrower than on most platforms, on purpose. Knowing what it can and cannot do saves you filing something that was never going to go anywhere.',
  sections: [
    {
      id: 'five-reasons',
      heading: 'There are five reasons, and only five',
      blocks: [
        p(
          'These are the only reasons you can pick, and the only things a Council team is allowed to judge. There is no sixth, and there is no general “this breaks the rules” option.',
        ),
        // Built from the shared list so this page and the report form cannot
        // disagree about what the five are.
        bullets(MODERATION_CATEGORIES.map((c) => [{ text: CATEGORY_LABELS[c] }])),
        p(
          'Content you find rude, wrong, offensive or stupid is not on that list. That is the deliberate cost of the design, not an oversight.',
        ),
      ],
    },
    {
      id: 'one-report',
      heading: 'One report does not do anything on its own',
      blocks: [
        p(
          'A single report changes nothing, and neither does a handful. Reports have to build up weight and broadly agree on the same reason before a case opens. Until then the person you reported is told nothing, and no label appears anywhere.',
        ),
        p(
          'This is what stops a group of new accounts from silencing someone by piling on. It also means reports that disagree about the reason cancel each other out rather than adding up.',
        ),
      ],
    },
    {
      id: 'once-a-case-opens',
      heading: 'What happens once a case opens',
      blocks: [
        bullets([
          [
            {
              text: 'A team of moderators is drawn at random for that one case, then dissolved afterwards. Nobody sits on a standing panel.',
            },
          ],
          [
            {
              text: 'The post stays fully visible the whole time, carrying an “Under Review” label. Nothing is hidden before a decision.',
            },
          ],
          [
            {
              text: 'The team votes, and the decision comes with a written explanation and the real tally.',
            },
          ],
          [
            {
              text: 'The member it was about can appeal. An appeal is heard by a completely fresh team, with every member of the first one excluded.',
            },
          ],
        ]),
      ],
    },
    {
      id: 'reporting-well',
      heading: 'Reporting well',
      blocks: [
        bullets([
          [
            {
              text: 'Pick the reason you actually mean. A wrong category makes agreement harder, not easier.',
            },
          ],
          [{ text: 'Add context if the problem is not obvious from the post itself.' }],
          [
            {
              text: 'Do not ask other people to report the same thing. Coordinated reporting is itself one of the five categories.',
            },
          ],
        ]),
      ],
    },
    {
      id: 'legal-floor',
      heading: 'Some things do not go to the Council at all',
      blocks: [
        p(
          'A short, closed list of material is unlawful to host. It is not judged and not voted on, because a vote cannot make it lawful. And one report is enough.',
        ),
        p(
          'What you cannot appeal is the rule itself: we are not permitted to host this whatever our own principles say, and no Council team has the authority to allow it. What you can contest is the identification: that this is not the material we took it to be, that it is the wrong account, or that an order does not say what we read it to say. If you were caught by this wrongly, say so. That is exactly what the challenge is for.',
        ),
        {
          kind: 'callout',
          runs: [
            { text: 'Reporting that does not need an account: ' },
            { label: 'report illegal content', href: '/report-illegal' },
            { text: '. You do not need to be the person affected.' },
          ],
        },
        {
          kind: 'paragraph',
          runs: [
            { text: 'The full rules are in the ' },
            { label: 'Moderation Policy', href: '/policies/moderation' },
            { text: ' and the ' },
            { label: 'Community Guidelines', href: '/policies/guidelines' },
            { text: '.' },
          ],
        },
      ],
    },
  ],
};
